import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "@/lib/api";
import { ArrowRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { SERVICE_META, SERVICE_ORDER } from "@/lib/serviceMeta";
import usePageSEO from "@/hooks/usePageSEO";

export default function Services() {
  const [services, setServices] = useState([]);

  usePageSEO({
    title: "Services — Insurance & Wealth Strategies",
    description:
      "Ten service categories from Lifespan Wealth Partners: life insurance, annuities, health, Medicare, LTC, disability, business, college, estate, and comprehensive strategy.",
  });

  useEffect(() => {
    let cancelled = false;
    apiGet("/services").then((d) => { if (!cancelled) setServices(d || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const bySlug = Object.fromEntries(services.map((s) => [s.slug, s]));
  const ordered = SERVICE_ORDER.map((slug) => bySlug[slug]).filter(Boolean);

  return (
    <div data-testid="services-index" className="min-h-screen bg-lw-ivory">
      <Nav />
      <main className="pt-28 sm:pt-32 lg:pt-36 pb-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <span className="lw-eyebrow">Services</span>
          <h1
            data-testid="services-headline"
            className="mt-3 font-serif-display text-4xl sm:text-5xl lg:text-6xl text-lw-navy leading-[1.05] max-w-3xl"
          >
            Ten service categories, one coordinated strategy.
          </h1>
          <p className="mt-5 text-base sm:text-lg text-lw-muted leading-relaxed max-w-2xl">
            Each service is a category of solutions. Pick the one that matches
            what you&apos;re solving for — or start with Comprehensive Wealth
            Strategy and we&apos;ll sequence the layers together.
          </p>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {ordered.map((s) => {
              const meta = SERVICE_META[s.slug] || {};
              const Icon = meta.Icon;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`service-tile-${s.slug}`}
                  className="lw-card p-6 sm:p-7 flex flex-col h-full group"
                >
                  <div className="flex items-start justify-between">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center text-lw-navy"
                      style={{ background: "linear-gradient(135deg, rgba(11,37,69,0.06) 0%, rgba(212,162,76,0.14) 100%)" }}
                    >
                      {Icon && <Icon size={20} />}
                    </div>
                    {s.medicare_gated && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-lw-navy text-lw-ivory text-[10px] font-semibold uppercase tracking-[0.14em]">
                        Coming Soon
                      </span>
                    )}
                  </div>
                  <h3 className="mt-5 font-serif-display text-2xl text-lw-navy leading-tight">
                    {s.name}
                  </h3>
                  <p className="mt-2 text-sm text-lw-muted leading-relaxed">
                    {meta.tile || s.hero_subhead}
                  </p>
                  <div className="mt-auto pt-6 flex items-center gap-1.5 text-lw-navy font-semibold text-sm group-hover:gap-3 transition-all">
                    Explore <ArrowRight size={16} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
